import { useEffect, useRef } from "react";
import axios from "axios";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { isExpoPushToken, resolveNativeRoute } from "./nativeRoutes";
import { getDevice, postToNative, registeredKey, setDevice } from "./nativeSession";

// Bridge to the Expo shell (react-native-webview). Inert in a normal browser.
// Web -> native: window.ReactNativeWebView.postMessage(JSON)  { type: ready | auth{authenticated,userId} | logout }
// Native -> web: window event "anike:native" with detail { type: push-token, token, platform } or { type: navigate, target: { type, id } }
// "auth" follows the signed-in user (set after login or after the session was restored), so a silent
// access-token refresh sends nothing. "logout" is sent by announceSessionEnded() only when the session ends.
// No token or cookie ever crosses the bridge; this app registers/unregisters the device with its own session.
export default function NativeBridge() {
  const history = useHistory();
  const userId = useSelector(({ auth }) => auth.user && auth.user.user && auth.user.user._id, shallowEqual);

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const registerDevice = () => {
    const current = getDevice();
    const id = userIdRef.current;
    if (!current || !id) return;
    const key = `${id}:${current.token}`;
    if (registeredKey.get() === key) return;
    axios
      .post("/v2/user/push-token", { token: current.token, platform: current.platform })
      .then(() => registeredKey.set(key))
      .catch(() => { /* retried the next time the app reports the token */ });
  };

  useEffect(() => {
    if (!window.ReactNativeWebView) return undefined;

    const onNative = event => {
      const message = event && event.detail;
      if (!message || typeof message.type !== "string") return;

      if (message.type === "push-token" && isExpoPushToken(message.token)) {
        setDevice({
          token: message.token,
          platform: message.platform === "ios" || message.platform === "android" ? message.platform : undefined
        });
        registerDevice();
      } else if (message.type === "navigate" && userIdRef.current) {
        const path = resolveNativeRoute(message.target);
        if (path) history.push(path);
      }
    };

    window.addEventListener("anike:native", onNative);
    postToNative({ type: "ready" });
    return () => window.removeEventListener("anike:native", onNative);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    postToNative({ type: "auth", authenticated: Boolean(userId), userId: userId ? String(userId) : undefined });
  }, [userId]);

  return null;
}
